import { z } from 'zod';

import {
  TestQuestionOptionTranslationsSchema,
  TestQuestionTranslationsSchema,
} from '../test-question/test-question';

/** How many options one question may offer — enough for a matrix, few enough to read. */
export const QUESTION_MAX_OPTIONS = 10;
export const QUESTION_MIN_OPTIONS = 2;

/**
 * An option as it is authored: the copy in both locales, plus whether it is
 * right.
 *
 * Options carry **no id on write**. The whole option set is submitted with its
 * question and replaced wholesale, because a partial option edit has to answer
 * "what happens to attempts already scored against the old set?" for every
 * possible partial — and the honest answer, that a scored attempt keeps the
 * score it was given, is what replacing the set makes obvious rather than
 * subtle. `test_attempts.answers` records the option ids that were chosen, so an
 * old attempt stays readable even after the set is rewritten.
 */
export const TestQuestionOptionWriteSchema = z
  .object({
    isCorrect: z.boolean(),
    translations: TestQuestionOptionTranslationsSchema,
  })
  .strict();

export type TestQuestionOptionWrite = z.infer<typeof TestQuestionOptionWriteSchema>;

export const TestQuestionWritableShape = {
  allowsMultiple: z.boolean(),
  translations: TestQuestionTranslationsSchema,
  options: z
    .array(TestQuestionOptionWriteSchema)
    .min(QUESTION_MIN_OPTIONS)
    .max(QUESTION_MAX_OPTIONS),
};

/**
 * Creating a question (`courses.manage`). `testId` comes from the route.
 *
 * The answer key is **not** validated by a `.refine` on this object: an object
 * refinement cannot attach its error to `options`, and — as P16b found for
 * object-valued settings — it re-runs against a whole the update schema does not
 * have. The service calls {@link hasValidAnswerKey} and answers
 * `test_question_options_invalid`.
 */
export const TestQuestionCreateSchema = z.object({ ...TestQuestionWritableShape }).strict();

export type TestQuestionCreateRequest = z.infer<typeof TestQuestionCreateSchema>;

/**
 * Whether an option set can actually be answered (matrix row 20's neighbour: a
 * test that cannot be passed is worse than one that is withdrawn).
 *
 * Single-answer: **exactly one** correct option — zero makes the question
 * unpassable, and two make it unanswerable, since the member may pick only one.
 * Multi-answer: **at least one**, and all of them is legitimate ("select every
 * statement that is true" where they all are).
 *
 * Takes the flags rather than the option objects so both the create path and the
 * update path can call it without building a shape neither of them has yet.
 */
export function hasValidAnswerKey(
  allowsMultiple: boolean,
  correctness: readonly boolean[],
): boolean {
  const correct = correctness.filter(Boolean).length;

  return allowsMultiple ? correct >= 1 : correct === 1;
}
